import type {
  CourseCertificateEvidenceListModel,
  CourseCertificateEvidenceModel,
  CourseCertificateQueryParams,
  CourseCertificateStatusResponse,
  CourseCertificateUploadBatchResponse,
  PaginatedResponse,
} from "@/features/cle-compliance/models/courseCertificate";
import type { EvidenceAttachmentModel } from "@/features/cle-compliance/models/evidenceAttachment";
import { del, get, getBlob, post, postForm } from "./util";

export const COURSE_CERTIFICATES_QUERY_KEY = ["course-certificates"] as const;

export const COURSE_CERTIFICATES_LIST_QUERY_KEY = [
  ...COURSE_CERTIFICATES_QUERY_KEY,
  "list",
] as const;

export const createCourseCertificateDetailQueryKey = (id: string) =>
  [...COURSE_CERTIFICATES_QUERY_KEY, "detail", id] as const;

export const createCourseCertificateFileQueryKey = (id: string) =>
  [...COURSE_CERTIFICATES_QUERY_KEY, "files", id] as const;

const COURSE_CERTIFICATES_QUERY = "documents/course-certificates";

const createCourseCertificateDownloadQuery = (id: string): string =>
  `${COURSE_CERTIFICATES_QUERY}/${id}/download`;

const createCourseCertificateAwaitingHumanReviewQuery = (id: string): string =>
  `${COURSE_CERTIFICATES_QUERY}/${id}/awaiting-human-review`;

const createCourseCertificatesForReportingQuery = (reportId: string): string =>
  `${COURSE_CERTIFICATES_QUERY}/available-for-period/${reportId}`;

const createCourseCertificatesForInjunctionQuery = (
  injunctionId: string,
): string =>
  `${COURSE_CERTIFICATES_QUERY}/available-for-injunction/${injunctionId}`;

const createCourseCertificatesForImpositionQuery = (
  impositionId: string,
): string =>
  `${COURSE_CERTIFICATES_QUERY}/available-for-imposition/${impositionId}`;

const createSearchParams = ({
  page,
  page_size,
  reported,
}: CourseCertificateQueryParams): string => {
  const searchParams = new URLSearchParams();

  if (reported != null) {
    searchParams.set("reported", String(reported));
  }

  if (page != null) {
    searchParams.set("page", String(page));
  }

  if (page_size != null) {
    searchParams.set("page_size", String(page_size));
  }

  return searchParams.toString();
};

const createListQuery = (params: CourseCertificateQueryParams = {}): string => {
  const queryString = createSearchParams(params);

  return queryString
    ? `${COURSE_CERTIFICATES_QUERY}?${queryString}`
    : COURSE_CERTIFICATES_QUERY;
};

export const fetchCourseCertificates = (
  params: CourseCertificateQueryParams = {},
): Promise<PaginatedResponse<CourseCertificateEvidenceListModel>> =>
  get<PaginatedResponse<CourseCertificateEvidenceListModel>>({
    query: createListQuery(params),
  });

export const fetchCourseCertificatesForReporting = (
  reportId: string,
): Promise<CourseCertificateEvidenceListModel[]> =>
  get<CourseCertificateEvidenceListModel[]>({
    query: createCourseCertificatesForReportingQuery(reportId),
  });

export const fetchCourseCertificatesForInjunction = (
  injunctionId: string,
): Promise<CourseCertificateEvidenceListModel[]> =>
  get<CourseCertificateEvidenceListModel[]>({
    query: createCourseCertificatesForInjunctionQuery(injunctionId),
  });

export const fetchCourseCertificatesForImposition = (
  impositionId: string,
): Promise<CourseCertificateEvidenceListModel[]> =>
  get<CourseCertificateEvidenceListModel[]>({
    query: createCourseCertificatesForImpositionQuery(impositionId),
  });

export const fetchCourseCertificateById = (
  id: string,
): Promise<CourseCertificateEvidenceModel> =>
  get<CourseCertificateEvidenceModel>({
    query: `${COURSE_CERTIFICATES_QUERY}/${id}`,
  });

export const deleteCourseCertificate = async (id: string): Promise<void> => {
  await del({
    query: `${COURSE_CERTIFICATES_QUERY}/${id}`,
  });
};

export const setCourseCertificateAwaitingHumanReview = (
  id: string,
): Promise<CourseCertificateStatusResponse> =>
  post<CourseCertificateStatusResponse>({
    query: createCourseCertificateAwaitingHumanReviewQuery(id),
    body: {},
  });

export type CourseCertificateUploadRequestOptions = {
  signal?: AbortSignal;
  timeoutMs?: number;
};

/** Uploads one chunk of a selection. The caller splits and paces the chunks;
 * see planCourseCertificateUpload and useCourseCertificateBatchUpload. */
export const uploadCourseCertificatesBatch = async (
  files: File[],
  { signal, timeoutMs }: CourseCertificateUploadRequestOptions = {},
): Promise<CourseCertificateUploadBatchResponse> => {
  const formData = new FormData();

  files.forEach((file) => {
    formData.append("files", file);
  });

  return postForm<CourseCertificateUploadBatchResponse>({
    query: `${COURSE_CERTIFICATES_QUERY}/upload-batch`,
    body: formData,
    signal,
    timeoutMs,
  });
};

export const fetchCourseCertificateDocumentFile = (id: string): Promise<Blob> =>
  getBlob({
    query: createCourseCertificateDownloadQuery(id),
  });

/* --- Supplerende dokumentation (bilag) ---------------------------------
 *
 * Bilagene ligger på kursusbevisets detaljesvar, så der er ingen selvstændig
 * liste-query at holde i sync: mutationerne invaliderer bevisets egen. */

const createAttachmentsQuery = (evidenceId: string): string =>
  `${COURSE_CERTIFICATES_QUERY}/${evidenceId}/attachments`;

const createAttachmentQuery = (
  evidenceId: string,
  attachmentId: string,
): string => `${createAttachmentsQuery(evidenceId)}/${attachmentId}`;

export const uploadCourseCertificateAttachment = (
  evidenceId: string,
  file: File,
): Promise<EvidenceAttachmentModel> => {
  const formData = new FormData();
  formData.append("file", file);

  return postForm<EvidenceAttachmentModel>({
    query: createAttachmentsQuery(evidenceId),
    body: formData,
  });
};

export const deleteCourseCertificateAttachment = async (
  evidenceId: string,
  attachmentId: string,
): Promise<void> => {
  await del({
    query: createAttachmentQuery(evidenceId, attachmentId),
  });
};

export const fetchCourseCertificateAttachmentFile = (
  evidenceId: string,
  attachmentId: string,
): Promise<Blob> =>
  getBlob({
    query: `${createAttachmentQuery(evidenceId, attachmentId)}/download`,
  });
